// ============================================================================
// Gestes « angles associés » et « racines » de la feuille « vérifier chaque affirmation »
// (moteur v37, refonte lot 6.9 — docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// Fonctions PURES sur deux membres d'un maillon déjà reconnu VRAI (même valeur exacte) :
// elles disent seulement quel geste le maillon montre.
//
//   Angles associés     cos(π − π ÷ 4) → −cos(π ÷ 4), sin(π ÷ 2 − x) → cos(x)…   1 HORA
//   Lire sur le cercle  sin(5π ÷ 6) → 1 ÷ 2 (angle hors de [0 ; π ÷ 2])            1 HORA
//   Écrire comme une racine   3 → √9, 0,2 → √(0,04)                                 1 HORA
//   Simplifier la racine      √40 → 2√10                                            1 HORA
//
// v39 (refonte lot 6.13) : trois gestes de plus et la FORME d'une affirmation
//
//   Décomposer               84 → 2 × 42, 2 × 42 → 2 × 2 × 21 (un facteur en deux)      1 HORA
//   Regrouper en puissance   2 × 2 × 3 × 7 → 2² × 3 × 7                                1 HORA
//   Puissance de 10          45 000 → 4,5 × 10⁴, 0,5 × 10⁻³ → 5 × 10⁻⁴                   1 HORA
//   Fraction décimale        0,125 → 125 ÷ 1000                                        1 HORA
//
// Une affirmation peut exiger une FORME de son membre de droite (le choix du QCM) :
// fraction irréductible, notation scientifique, décomposition en facteurs premiers
// (`hasClaimForm`). Elle est vraie si l'égalité est vraie ET si l'écriture a cette forme.
//
// v40 (refonte lot 6.14) : une affirmation peut porter une PROPRIÉTÉ d'une écriture seule
// (« 29 » nombre premier, « 6 ÷ 9 » fraction irréductible, « 4182 » divisible par 6) ; un
// geste de plus :
//
//   Division euclidienne     29 → 2 × 14 + 1 (reste plus petit que le diviseur)        1 HORA
//
// Valeur interdite : une affirmation dont un membre divise par 0 (« 5 ÷ 9 − 2 ÷ 9 = 3 ÷ 0 »,
// « (x + 1) ÷ (x − 2) = 0 » pour x = 2) est FAUSSE : le juge la tranche dès qu'un « a ÷ 0 »
// est écrit (kernel-claims.ts). Une feuille qui s'en sert est aussi en v37.
//
// Un maillon d'angles associés relie f(u) à ±g(v), u et v multiples rationnels de π :
// même fonction et u ≡ ±v (mod π), ou cosinus ↔ sinus et u ≡ ±v + π ÷ 2 (mod π). Un
// angle seulement réécrit (cos(3π ÷ 4) → cos(π − π ÷ 4)) n'est pas un geste.
// Ce module ne dépend que du noyau (pas du modèle) : model.ts s'en sert pour la version.
// ============================================================================

import * as Q from './kernel/exact';
import type { Rat } from './kernel/exact';
import { parseStatement, type Term } from './kernel/term';
import { piCoef } from './kernel/trig';

export type ClaimsGesture = 'trig' | 'circle' | 'trigValue' | 'rootWrite' | 'rootSimplify';
/** gestes du moteur v39 : décomposer en facteurs, regrouper en puissance, puissance de 10 */
export type ClaimsFormGesture =
  | 'decompose'
  | 'powerGroup'
  | 'powerMerge'
  | 'sciWrite'
  | 'decimalFrac'
  | 'cancel'
  | 'euclid';
/**
 * la forme exigée d'une écriture (moteur v39) ; « nombre premier » (v40) seulement pour une
 * affirmation de propriété (une écriture seule, sans relation)
 */
export const CLAIM_FORMS = ['irreducible', 'scientific', 'primes', 'prime'] as const;
export type ClaimForm = (typeof CLAIM_FORMS)[number];

type TrigFn = 'cos' | 'sin' | 'tan';
interface SignedFn {
  s: 1 | -1;
  f: TrigFn;
  /** angle ÷ π */
  u: Rat;
}

const isNum = (t: Term): t is { k: 'num'; v: Rat } => t.k === 'num';
const isMinusOne = (t: Term): boolean => isNum(t) && Q.eq(t.v, Q.rat(-1));

/** ± f(u), u multiple rationnel de π */
function signedFn(t: Term): SignedFn | null {
  if (t.k === 'fn' && (t.f === 'cos' || t.f === 'sin' || t.f === 'tan')) {
    const u = piCoef(t.a);
    return u ? { s: 1, f: t.f, u } : null;
  }
  if (t.k === 'mul' && t.c.length === 2 && isMinusOne(t.c[0]!)) {
    const x = signedFn(t.c[1]!);
    return x ? { ...x, s: x.s === 1 ? -1 : 1 } : null;
  }
  return null;
}

/** q est un entier (multiple de π) */
const isInt = (q: Rat): boolean => Q.isInteger(q);
/** q est un multiple de 1/2 (angle « droit » : 0, π ÷ 2, π…) */
const isHalfInt = (q: Rat): boolean => Q.isInteger(Q.mul(q, Q.rat(2)));
/** angle dans [0 ; π ÷ 2] */
const firstQuadrant = (u: Rat): boolean => Q.sign(u) >= 0 && Q.cmp(u, Q.rat(1, 2)) <= 0;

/** f(u) et g(v) sont reliés par une formule d'angles associés */
function associated(a: SignedFn, b: SignedFn): boolean {
  const sum = Q.add(a.u, b.u);
  const diff = Q.sub(a.u, b.u);
  if (a.f === b.f) {
    if (Q.eq(a.u, b.u)) return false; // même angle : seulement réécrit
    return isInt(sum) || isInt(diff);
  }
  if (a.f === 'tan' || b.f === 'tan') return false;
  // cos ↔ sin : u ≡ ±v + π ÷ 2 (mod π)
  const half = Q.rat(1, 2);
  return isInt(Q.sub(sum, half)) || isInt(Q.sub(diff, half)) || isInt(Q.add(diff, half));
}

/** une valeur rationnelle écrite (« 3 », « 0,2 », « 1 ÷ 2 ») */
function ratWritten(t: Term): Rat | null {
  if (isNum(t)) return t.v;
  if (t.k === 'div' && isNum(t.a) && isNum(t.b)) return Q.div(t.a.v, t.b.v);
  return null;
}

/** une racine d'entier avec un facteur carré, éventuellement multipliée : « √40 », « 3√8 » */
function reducibleRootOf(t: Term): bigint | null {
  let r: Term = t;
  if (t.k === 'mul' && t.c.length === 2 && isNum(t.c[0]!)) r = t.c[1]!;
  if (r.k !== 'fn' || r.f !== 'sqrt' || !isNum(r.a) || !Q.isInteger(r.a.v)) return null;
  const n = r.a.v.n;
  if (n < 4n) return null;
  const p = Q.squarefreePart(n);
  return p && p.square > 1n ? n : null;
}

/** « k√p » (p sans facteur carré, k entier ≥ 2) ou « √p » */
function simplifiedRoot(t: Term): boolean {
  let r: Term = t;
  if (t.k === 'mul' && t.c.length === 2 && isNum(t.c[0]!)) {
    if (!Q.isInteger(t.c[0]!.v)) return false;
    r = t.c[1]!;
  }
  if (r.k !== 'fn' || r.f !== 'sqrt' || !isNum(r.a) || !Q.isInteger(r.a.v)) return false;
  const p = Q.squarefreePart(r.a.v.n);
  return !!p && p.square === 1n;
}

/**
 * Le geste d'un maillon VRAI a → b, ou null (le maillon relève des autres règles).
 * `bIsValue` : b est une valeur (le juge le sait déjà : nombre, fraction, irrationnel
 * sans opération).
 */
export function claimsGesture(a: Term, b: Term, bIsValue: boolean): ClaimsGesture | null {
  const fa = signedFn(a);
  if (fa) {
    const fb = signedFn(b);
    if (fb) return associated(fa, fb) ? 'trig' : null;
    if (!bIsValue || isHalfInt(fa.u)) return null;
    // −cos(π ÷ 4) → −√2 ÷ 2 : un calcul ; sin(5π ÷ 6) → 1 ÷ 2 : lu sur le cercle
    if (firstQuadrant(fa.u)) return fa.s === -1 ? 'trigValue' : null;
    return 'circle';
  }
  const v = ratWritten(a);
  if (v && Q.sign(v) > 0 && b.k === 'fn' && b.f === 'sqrt' && ratWritten(b.a)) return 'rootWrite';
  if (reducibleRootOf(a) && simplifiedRoot(b)) return 'rootSimplify';
  return null;
}

/* ------------------------------ formes (v39) ------------------------------ */

/** un entier écrit (« 7 », « −3 ») */
function intValue(t: Term): bigint | null {
  if (isNum(t) && Q.isInteger(t.v)) return t.v.n;
  if (t.k === 'mul' && t.c.length === 2 && isMinusOne(t.c[0]!)) {
    const x = intValue(t.c[1]!);
    return x === null ? null : -x;
  }
  return null;
}

const gcdB = (a: bigint, b: bigint): bigint => {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y) [x, y] = [y, x % y];
  return x;
};

/** n est premier (n raisonnable : au plus 10¹², les QCM restent loin en dessous) */
export function isPrimeInt(n: bigint): boolean {
  if (n < 2n || n > 1_000_000_000_000n) return false;
  if (n < 4n) return true;
  if (n % 2n === 0n) return false;
  for (let d = 3n; d * d <= n; d += 2n) if (n % d === 0n) return false;
  return true;
}

interface IntFactor {
  b: bigint;
  e: number;
}

/** facteurs d'un produit d'entiers écrit (« 2² × 3 × 7 », « 84 ») ; null sinon */
function intFactors(t: Term): IntFactor[] | null {
  const fs = t.k === 'mul' ? t.c : [t];
  const out: IntFactor[] = [];
  for (const f of fs) {
    if (isNum(f) && Q.isInteger(f.v) && f.v.n > 0n) out.push({ b: f.v.n, e: 1 });
    else if (
      f.k === 'pow' &&
      isNum(f.a) &&
      Q.isInteger(f.a.v) &&
      f.a.v.n > 0n &&
      isNum(f.b) &&
      Q.isInteger(f.b.v) &&
      f.b.v.n >= 1n &&
      f.b.v.n <= 64n
    )
      out.push({ b: f.a.v.n, e: Number(f.b.v.n) });
    else return null;
  }
  return out;
}

const factorKey = (f: IntFactor): string => `${f.b}^${f.e}`;
function sameMultiset(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort();
  const y = [...b].sort();
  return x.every((k, i) => k === y[i]);
}

/** « m × 10ⁿ » : la mantisse et l'exposant écrits ; null sinon */
function powerOfTen(t: Term): { m: Rat; n: bigint } | null {
  let body = t;
  let sign: Rat = Q.ONE;
  if (t.k === 'mul' && t.c.length === 2 && isMinusOne(t.c[0]!)) {
    body = t.c[1]!;
    sign = Q.rat(-1);
  }
  if (body.k !== 'mul' || body.c.length !== 2) return null;
  const [x, y] = body.c as [Term, Term];
  const tryPair = (m: Term, p: Term): { m: Rat; n: bigint } | null => {
    if (!isNum(m) || p.k !== 'pow' || !isNum(p.a) || !Q.eq(p.a.v, Q.rat(10))) return null;
    const n = intValue(p.b);
    return n === null ? null : { m: Q.mul(sign, m.v), n };
  };
  return tryPair(x, y) ?? tryPair(y, x);
}

/** L'écriture t a-t-elle la forme exigée ? */
export function hasClaimForm(t: Term, form: ClaimForm): boolean {
  switch (form) {
    case 'irreducible': {
      if (intValue(t) !== null) return true;
      let f: Term = t;
      if (t.k === 'mul' && t.c.length === 2 && isMinusOne(t.c[0]!)) f = t.c[1]!;
      if (f.k !== 'div') return false;
      const p = intValue(f.a);
      const q = intValue(f.b);
      return p !== null && q !== null && q > 1n && gcdB(p, q) === 1n;
    }
    case 'scientific': {
      const s = powerOfTen(t);
      if (!s) return false;
      const a = Q.sign(s.m) < 0 ? Q.neg(s.m) : s.m;
      return Q.cmp(a, Q.ONE) >= 0 && Q.cmp(a, Q.rat(10)) < 0;
    }
    case 'primes': {
      const fs = intFactors(t);
      if (!fs || !fs.length) return false;
      const bases = new Set(fs.map((f) => f.b));
      return bases.size === fs.length && fs.every((f) => isPrimeInt(f.b));
    }
    case 'prime': {
      const n = intValue(t);
      return n !== null && isPrimeInt(n);
    }
  }
}

/* ------------------- fraction irréductible par les facteurs premiers (v53) ------------------- */
// Moteur v53 (refonte lot 6.57) : une fraction se montre irréductible par les DÉCOMPOSITIONS
// de son numérateur et de son dénominateur en facteurs premiers. Un geste de plus :
//
//   Décomposer en facteurs premiers   16 ÷ 51 → 2⁴ ÷ (3 × 17)                   2 HORA
//
// et une fraction écrite avec ses facteurs (« 2² ÷ (3 × 5) ») est irréductible quand son
// numérateur et son dénominateur n'ont aucun facteur commun : elle se compare à la fraction
// irréductible écrite par la VALEUR de son numérateur et de son dénominateur (4 ÷ 15).

/** la valeur d'un produit d'entiers écrit (« 2² × 3 », « 84 ») ; null sinon */
function factorsValue(t: Term): bigint | null {
  const fs = intFactors(t);
  if (!fs) return null;
  return fs.reduce((v, f) => v * f.b ** BigInt(f.e), 1n);
}

/**
 * « 2² ÷ (3 × 5) », « (2 × 3) ÷ 9 » : une fraction dont le numérateur ou le dénominateur est
 * écrit en PRODUIT (ou en puissance) d'entiers ; la valeur de chacun. Null pour une fraction
 * « p ÷ q » d'entiers écrits (forme v39-v40, inchangée).
 */
export function factoredFraction(t: Term): { p: bigint; q: bigint } | null {
  if (t.k !== 'div') return null;
  if (intValue(t.a) !== null && intValue(t.b) !== null) return null;
  const p = factorsValue(t.a);
  const q = factorsValue(t.b);
  return p === null || q === null || q === 0n ? null : { p, q };
}

/** une fraction (écrite « p ÷ q » ou avec ses facteurs) est-elle irréductible ? */
export function irreducibleTruth(t: Term): boolean {
  if (hasClaimForm(t, 'irreducible')) return true;
  const f = factoredFraction(t);
  return !!f && f.q > 1n && gcdB(f.p, f.q) === 1n;
}

/** w (« p ÷ q » irréductible) a-t-elle le numérateur et le dénominateur de la fraction f ? */
export function sameFractionValues(w: Term, f: { p: bigint; q: bigint }): boolean {
  if (w.k !== 'div') return false;
  return intValue(w.a) === f.p && intValue(w.b) === f.q;
}

/** un produit de nombres PREMIERS écrit (« 2⁴ », « 3 × 17 », « 7 ») ; null sinon */
function primeProduct(t: Term): bigint | null {
  const fs = intFactors(t);
  if (!fs || !fs.length || !fs.every((f) => isPrimeInt(f.b))) return null;
  return factorsValue(t);
}

/**
 * Décomposer en facteurs premiers le numérateur et le dénominateur d'une fraction « p ÷ q »
 * d'entiers écrits pour montrer qu'elle est IRRÉDUCTIBLE : p ÷ q → A ÷ B, A et B produits de
 * nombres premiers de valeurs p et q, SANS facteur premier commun, au moins un des deux
 * réécrit. (Avec un facteur commun, c'est « Facteur commun » puis « Simplifier », comme avant.)
 */
export function primeFractionStep(a: Term, b: Term): boolean {
  if (a.k !== 'div' || b.k !== 'div') return false;
  const pa = intValue(a.a);
  const qa = intValue(a.b);
  const pb = primeProduct(b.a);
  const qb = primeProduct(b.b);
  if (pa === null || qa === null || pb === null || qb === null) return false;
  if (pa !== pb || qa !== qb || qa <= 1n || pa < 1n) return false;
  const primesOf = (t: Term) => new Set((intFactors(t) ?? []).map((f) => f.b));
  const top = primesOf(b.a);
  if ([...primesOf(b.b)].some((x) => top.has(x))) return false;
  const same = (x: Term, y: Term) => termShape(x) === termShape(y);
  return !same(a.a, b.a) || !same(a.b, b.b);
}

/** Le corrigé décompose-t-il une fraction en facteurs premiers ? (moteur v53) */
export function pathUsesFractionPrimes(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map(exprOf);
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++) if (primeFractionStep(ms[i]!, ms[i + 1]!)) return true;
  }
  return false;
}

/** une affirmation « fraction irréductible » écrite avec ses facteurs (« 2² ÷ (3 × 5) », v53) */
export function isFactoredIrreducibleClaim(c: {
  rels: readonly string[];
  form?: string;
  at?: Record<string, string>;
}): boolean {
  if (c.form !== 'irreducible' || c.rels.length !== 1 || (c.at && Object.keys(c.at).length))
    return false;
  const t = exprOf(c.rels[0]!);
  return !!t && factoredFraction(t) !== null;
}

/**
 * Deux écritures de même valeur dans la forme sont-elles la MÊME écriture ? (la forme est
 * unique : p ÷ q irréductible, m × 10ⁿ, les facteurs premiers — à l'ordre près)
 */
export function sameFormWriting(a: Term, b: Term, form: ClaimForm): boolean {
  if (form === 'primes') {
    const fa = intFactors(a);
    const fb = intFactors(b);
    return !!fa && !!fb && sameMultiset(fa.map(factorKey), fb.map(factorKey));
  }
  if (form === 'scientific') {
    const pa = powerOfTen(a);
    const pb = powerOfTen(b);
    return !!pa && !!pb && Q.eq(pa.m, pb.m) && pa.n === pb.n;
  }
  return termShape(a) === termShape(b);
}

/**
 * « d × q + r » ou « d × q » (entiers positifs) : les facteurs du produit et le reste ;
 * null sinon. Le diviseur est un facteur plus grand que le reste.
 */
export function euclidParts(t: Term): { factors: bigint[]; r: bigint } | null {
  const prod = (x: Term): bigint[] | null => {
    const fs = intFactors(x);
    if (!fs || fs.length < 2 || fs.some((f) => f.e !== 1)) return null;
    return fs.map((f) => f.b);
  };
  const p = prod(t);
  if (p) return { factors: p, r: 0n };
  if (t.k !== 'add' || t.c.length !== 2) return null;
  const [x, y] = t.c as [Term, Term];
  const f = prod(x);
  const r = intValue(y);
  if (!f || r === null || r < 0n) return null;
  return { factors: f, r };
}

/** a est un entier positif, b sa division par l'un des facteurs, reste plus petit que lui */
function isEuclid(a: Term, b: Term): boolean {
  const n = intValue(a);
  if (n === null || n < 1n || b.k !== 'add') return false;
  const e = euclidParts(b);
  return !!e && e.r > 0n && e.factors.some((d) => d >= 2n && e.r < d);
}

/** écriture d'un facteur, pour comparer deux produits (« 12 », « pow(10,3) ») */
function termShape(t: Term): string {
  switch (t.k) {
    case 'num':
      return Q.ratToText(t.v);
    case 'sym':
      return t.n;
    case 'pow':
      return `pow(${termShape(t.a)},${termShape(t.b)})`;
    case 'mul':
    case 'add':
      return `${t.k}(${t.c.map(termShape).join(',')})`;
    case 'div':
    case 'binom':
      return `${t.k}(${termShape(t.a)},${termShape(t.b)})`;
    case 'fn':
      return `${t.f}(${termShape(t.a)})`;
    default:
      return JSON.stringify(t);
  }
}

/** « aⁿ » (n rationnel écrit) ou « a » seul (exposant 1) : base et exposant */
function powOf(t: Term): { base: string; e: Rat } | null {
  if (t.k === 'pow') {
    const n = intValue(t.b);
    return n === null ? null : { base: termShape(t.a), e: Q.rat(n) };
  }
  if (isNum(t) || t.k === 'sym') return { base: termShape(t), e: Q.ONE };
  return null;
}

/** les facteurs d'un produit d'entiers, puissances déroulées (« 2² × 3 » → 2, 2, 3), sans 1 */
function flatFactors(t: Term): string[] | null {
  const fs = intFactors(t);
  if (!fs) return null;
  return fs.flatMap((f) => (f.b === 1n ? [] : Array<string>(f.e).fill(f.b.toString())));
}

/** b est a moins les facteurs de x (multiensembles) ; x rempli des facteurs retirés */
function removedFrom(a: string[], b: string[]): string[] | null {
  const left = [...a];
  for (const k of b) {
    const i = left.indexOf(k);
    if (i < 0) return null;
    left.splice(i, 1);
  }
  return left;
}

/**
 * Barrer les facteurs communs : (2 × 2 × 3) ÷ (2 × 3 × 5) → 2 ÷ 5, (2² × 3) ÷ (2 × 3 × 5)
 * → 2 ÷ 5 ; les mêmes facteurs retirés en haut et en bas. Seulement au-delà de ce que les
 * gestes d'avant savent déjà (moteur ≤ v38 : « (4 × 4) ÷ (4 × 9) → 4 ÷ 9 » se simplifie,
 * « 3⁵ ÷ 3³ → 3² » est un quotient de puissances) : au moins deux bases, et deux facteurs
 * barrés ou trois facteurs écrits d'un côté.
 */
function cancelsFactors(a: Term, b: Term): boolean {
  if (a.k !== 'div') return false;
  const fa = intFactors(a.a);
  const fd = intFactors(a.b);
  const na = flatFactors(a.a);
  const da = flatFactors(a.b);
  if (!fa || !fd || !na || !da) return false;
  if (new Set([...na, ...da]).size < 2) return false;
  const nb = flatFactors(b.k === 'div' ? b.a : b);
  const db = b.k === 'div' ? flatFactors(b.b) : [];
  if (!nb || !db) return false;
  const rn = removedFrom(na, nb);
  const rd = removedFrom(da, db);
  if (!rn || !rd || !rn.length || !sameMultiset(rn, rd)) return false;
  return rn.length >= 2 || fa.length >= 3 || fd.length >= 3;
}

/** Le geste d'un maillon VRAI a → b du moteur v39, ou null. */
export function claimsFormGesture(a: Term, b: Term): ClaimsFormGesture | null {
  if (cancelsFactors(a, b)) return 'cancel';
  if (isEuclid(a, b)) return 'euclid';
  const fa = intFactors(a);
  const fb = intFactors(b);
  if (fa && fb) {
    // un facteur n remplacé par deux entiers u × v (u, v ≥ 2)
    if (fb.length === fa.length + 1) {
      const ka = fa.map(factorKey);
      const kb = fb.map(factorKey);
      for (let i = 0; i < fa.length; i++) {
        const n = fa[i]!;
        if (n.e !== 1 || n.b < 4n) continue;
        const rest = ka.filter((_, j) => j !== i);
        for (let x = 0; x < fb.length; x++)
          for (let y = x + 1; y < fb.length; y++) {
            const u = fb[x]!;
            const v = fb[y]!;
            if (u.e !== 1 || v.e !== 1 || u.b < 2n || v.b < 2n || u.b * v.b !== n.b) continue;
            if (
              sameMultiset(
                rest,
                kb.filter((_, j) => j !== x && j !== y),
              )
            )
              return 'decompose';
          }
      }
    }
    // des facteurs égaux regroupés en puissance : 2 × 2 → 2² (les autres bases inchangées)
    if (fb.length < fa.length) {
      const bases = new Set([...fa, ...fb].map((f) => f.b));
      let merged = 0;
      const ok = [...bases].every((base) => {
        const xa = fa.filter((f) => f.b === base);
        const xb = fb.filter((f) => f.b === base);
        if (sameMultiset(xa.map(factorKey), xb.map(factorKey))) return true;
        const sum = xa.reduce((n, f) => n + f.e, 0);
        if (xa.length >= 2 && xb.length === 1 && xb[0]!.e === sum) {
          merged++;
          return true;
        }
        return false;
      });
      if (ok && merged > 0) return 'powerGroup';
    }
  }
  // deux puissances de même base d'un produit de trois facteurs ou plus, réunies :
  // 12 × 10⁵ × 10⁻² → 12 × 10³ (le reste du produit inchangé)
  if (a.k === 'mul' && a.c.length >= 3 && b.k === 'mul' && b.c.length === a.c.length - 1) {
    const bk = b.c.map(termShape);
    for (let i = 0; i < a.c.length; i++)
      for (let j = i + 1; j < a.c.length; j++) {
        const p = powOf(a.c[i]!);
        const q = powOf(a.c[j]!);
        if (!p || !q || p.base !== q.base || (a.c[i]!.k !== 'pow' && a.c[j]!.k !== 'pow')) continue;
        const rest = a.c.filter((_, k) => k !== i && k !== j).map(termShape);
        const e = Q.add(p.e, q.e);
        const idx = b.c.findIndex((f) => {
          const r = powOf(f);
          return !!r && f.k === 'pow' && r.base === p.base && Q.eq(r.e, e);
        });
        if (idx < 0) continue;
        if (
          sameMultiset(
            rest,
            bk.filter((_, k) => k !== idx),
          )
        )
          return 'powerMerge';
      }
  }
  // un décimal en fraction décimale : 0,125 → 125 ÷ 1000
  if (isNum(a) && !Q.isInteger(a.v) && b.k === 'div') {
    const p = intValue(b.a);
    const q = intValue(b.b);
    if (p !== null && q !== null && q >= 10n && /^10*$/.test(q.toString())) return 'decimalFrac';
  }
  // puissance de 10 : 45 000 → 4,5 × 10⁴, 45 × 10³ → 4,5 × 10⁴
  const pb = powerOfTen(b);
  if (pb && (isNum(a) || intValue(a) !== null)) return 'sciWrite';
  const pa = powerOfTen(a);
  if (pa && pb && pa.n !== pb.n) return 'sciWrite';
  return null;
}

/* ------------------------------ logarithme (v45) ------------------------------ */
// Moteur v45 (refonte lot 6.43) : les règles de ln dans une écriture à simplifier, un
// geste par maillon, sur UN terme d'une somme (les autres recopiés) :
//
//   Logarithme d'un produit    ln(2 × 5) ↔ ln(2) + ln(5)                               1 HORA
//   Logarithme d'un quotient   ln(72 ÷ 8) ↔ ln(72) − ln(8), ln(1 ÷ 4) → ln(1) − ln(4)   1 HORA
//   Logarithme d'une puissance ln(2³) ↔ 3ln(2), ln(e³) → 3ln(e)                        1 HORA
//   Logarithme d'une racine    ln(√8) ↔ (1 ÷ 2)ln(8)                                   1 HORA
//   Logarithme d'un inverse    ln(1 ÷ 4) ↔ −ln(4)                                      1 HORA
//   Valeur à connaître         ln(1) = 0, ln(e) = 1, ln(e³) = 3, ln(√e) = 1 ÷ 2       1 HORA
//   Écrire comme une puissance ln(8) → ln(2³), ln(√e) → ln(e^(1 ÷ 2))                  1 HORA
//   Décomposer                 ln(72) → ln(8 × 9)                                      1 HORA
//
// La constante e s'écrit « e » (la feuille la lit exp(1)) ; ces fonctions acceptent les
// deux écritures. Les mêmes règles valent pour le logarithme DÉCIMAL log (log(10) = 1,
// log(10ⁿ) = n, log(2) + log(5) = log(10)) — jamais en mêlant ln et log.

export type ClaimsLnGesture =
  | 'lnProduct'
  | 'lnQuotient'
  | 'lnPower'
  | 'lnRoot'
  | 'lnInverse'
  | 'lnValue'
  | 'lnWrite'
  | 'decompose';

type LogFn = 'ln' | 'log';
interface LnTerm {
  /** la fonction : ln, ou log (logarithme décimal) */
  f: LogFn;
  /** coefficient rationnel écrit */
  c: Rat;
  arg: Term;
}

/** la valeur d'un facteur rationnel écrit (« 3 », « 1 ÷ 2 », « −2 », « (1 ÷ 2) × 3 ») */
function ratFactor(t: Term): Rat | null {
  if (isNum(t)) return t.v;
  if (t.k === 'div') {
    const a = ratFactor(t.a);
    const b = ratFactor(t.b);
    return a && b && !Q.isZero(b) ? Q.div(a, b) : null;
  }
  if (t.k === 'mul') {
    let c: Rat = Q.ONE;
    for (const f of t.c) {
      const r = ratFactor(f);
      if (!r) return null;
      c = Q.mul(c, r);
    }
    return c;
  }
  return null;
}

/** « c × ln(u) » (c rationnel écrit, éventuellement en plusieurs facteurs) */
function lnTermOf(t: Term): LnTerm | null {
  if (t.k === 'fn' && (t.f === 'ln' || t.f === 'log')) return { f: t.f, c: Q.ONE, arg: t.a };
  if (t.k === 'div') {
    const x = lnTermOf(t.a);
    const d = ratFactor(t.b);
    const c = x && d && !Q.isZero(d) ? Q.div(x.c, d) : null;
    return x && c ? { f: x.f, c, arg: x.arg } : null;
  }
  if (t.k === 'mul') {
    let c: Rat = Q.ONE;
    let ln: LnTerm | null = null;
    for (const f of t.c) {
      const r = ratFactor(f);
      if (r) {
        c = Q.mul(c, r);
        continue;
      }
      const x = lnTermOf(f);
      if (!x || ln) return null;
      ln = x;
    }
    return ln ? { f: ln.f, c: Q.mul(c, ln.c), arg: ln.arg } : null;
  }
  return null;
}

/** la constante e (« e » écrit, ou exp(1) une fois lu par la feuille) */
function isE(t: Term): boolean {
  if (t.k === 'sym') return t.n === 'e';
  return t.k === 'fn' && t.f === 'exp' && isNum(t.a) && Q.eq(t.a.v, Q.ONE);
}

/** l'exposant q d'une écriture de 10^q (1 → 0, 10 → 1, 10³ → 3) */
function tenExponent(t: Term): Rat | null {
  if (isNum(t) && Q.eq(t.v, Q.ONE)) return Q.ZERO;
  if (isNum(t) && Q.eq(t.v, Q.rat(10))) return Q.ONE;
  if (t.k === 'pow' && isNum(t.a) && Q.eq(t.a.v, Q.rat(10))) return ratFactor(t.b);
  return null;
}

/** la base de la fonction : u = base^q (e pour ln, 10 pour log) */
const baseExponent = (f: LogFn, t: Term): Rat | null =>
  f === 'ln' ? eExponent(t) : tenExponent(t);

/** l'exposant q d'une écriture de e^q (1 → 0, e → 1, e³ → 3, √e → 1/2, e^(1 ÷ 2)) */
function eExponent(t: Term): Rat | null {
  if (isNum(t) && Q.eq(t.v, Q.ONE)) return Q.ZERO;
  if (isE(t)) return Q.ONE;
  if (t.k === 'fn' && t.f === 'exp') return ratFactor(t.a);
  if (t.k === 'pow' && isE(t.a)) return ratFactor(t.b);
  if (t.k === 'fn' && t.f === 'sqrt' && isE(t.a)) return Q.rat(1, 2);
  return null;
}

/** la seule partie qui change entre deux écritures (le reste recopié), ou null */
function singleDiff(a: Term, b: Term): [Term, Term] | null {
  if (termShape(a) === termShape(b)) return null;
  if (a.k === 'fn' && b.k === 'fn' && a.f === b.f) return singleDiff(a.a, b.a) ?? [a, b];
  if ((a.k === 'pow' || a.k === 'div') && b.k === a.k) {
    const bb = b as typeof a;
    if (termShape(a.a) === termShape(bb.a)) return singleDiff(a.b, bb.b);
    if (termShape(a.b) === termShape(bb.b)) return singleDiff(a.a, bb.a);
    return [a, b];
  }
  if ((a.k === 'mul' || a.k === 'add') && b.k === a.k && a.c.length === b.c.length) {
    const diff = a.c.flatMap((c, i) => (termShape(c) === termShape(b.c[i]!) ? [] : [i]));
    if (diff.length === 1) return singleDiff(a.c[diff[0]!]!, b.c[diff[0]!]!);
  }
  return [a, b];
}

/** x = mᵏ (m entier ≥ 2, k entier ≠ 0, 1) : un nombre écrit comme une puissance (8 = 2³, 0,01 = 10⁻²) */
function intPowerWriting(x: Term, y: Term): boolean {
  if (!isNum(x) || Q.sign(x.v) <= 0 || y.k !== 'pow') return false;
  if (!isNum(y.a) || !Q.isInteger(y.a.v) || y.a.v.n < 2n) return false;
  const k = ratFactor(y.b);
  if (!k || !Q.isInteger(k) || k.n === 0n || k.n === 1n || k.n > 64n || k.n < -64n) return false;
  const p = Q.pow(y.a.v, Number(k.n));
  return !!p && Q.eq(p, x.v);
}

/** l'argument d'un logarithme réécrit en un geste (même valeur) */
function argRewrite(u: Term, v: Term): ClaimsLnGesture | null {
  const d = singleDiff(u, v);
  if (!d) return null;
  const [x, y] = d;
  if (intPowerWriting(x, y)) return 'lnWrite';
  // √e → e^(1 ÷ 2)
  if (x.k === 'fn' && x.f === 'sqrt' && isE(x.a)) {
    const q = eExponent(y);
    if (q && Q.eq(q, Q.rat(1, 2)) && y.k !== 'fn') return 'lnWrite';
    if (q && Q.eq(q, Q.rat(1, 2)) && y.k === 'fn' && y.f === 'exp') return 'lnWrite';
  }
  if (claimsFormGesture(x, y) === 'decompose') return 'decompose';
  return null;
}

/** u = aⁿ (ou e^q) et l'autre terme = n × c × ln(a) */
function powerOf(big: LnTerm, small: LnTerm): boolean {
  const u = big.arg;
  let n: Rat | null = null;
  if (u.k === 'pow' && termShape(u.a) === termShape(small.arg)) n = ratFactor(u.b);
  else if (big.f === 'ln' && u.k === 'pow' && isE(u.a) && isE(small.arg)) n = ratFactor(u.b);
  else if (big.f === 'ln' && u.k === 'fn' && u.f === 'exp' && isE(small.arg) && !isE(u))
    n = ratFactor(u.a);
  return !!n && Q.eq(Q.mul(big.c, n), small.c);
}

/** une écriture c ln(u) → c′ ln(u′) en un geste */
function lnPair(x: LnTerm, y: LnTerm): ClaimsLnGesture | null {
  if (x.f !== y.f) return null;
  if (Q.eq(x.c, y.c)) return argRewrite(x.arg, y.arg);
  if (powerOf(x, y) || powerOf(y, x)) return 'lnPower';
  const half = Q.rat(1, 2);
  const root = (big: LnTerm, small: LnTerm) =>
    big.arg.k === 'fn' &&
    big.arg.f === 'sqrt' &&
    termShape(big.arg.a) === termShape(small.arg) &&
    Q.eq(Q.mul(big.c, half), small.c);
  if (root(x, y) || root(y, x)) return 'lnRoot';
  const inverse = (big: LnTerm, small: LnTerm) => {
    if (big.arg.k !== 'div') return false;
    const one = ratFactor(big.arg.a);
    return (
      !!one &&
      Q.eq(one, Q.ONE) &&
      termShape(big.arg.b) === termShape(small.arg) &&
      Q.eq(Q.neg(big.c), small.c)
    );
  };
  if (inverse(x, y) || inverse(y, x)) return 'lnInverse';
  return null;
}

/** les facteurs d'un produit, à plat (formes) */
function flatShapes(t: Term): string[] {
  return t.k === 'mul' ? t.c.flatMap(flatShapes) : [termShape(t)];
}

/** c ln(u × v) ↔ c ln(u) + c ln(v) ; c ln(u ÷ v) ↔ c ln(u) − c ln(v) */
function lnSplit(w: LnTerm, u: LnTerm, v: LnTerm): ClaimsLnGesture | null {
  if (u.f !== w.f || v.f !== w.f) return null;
  if (w.arg.k === 'mul' && Q.eq(u.c, w.c) && Q.eq(v.c, w.c)) {
    if (sameMultiset(flatShapes(w.arg), [...flatShapes(u.arg), ...flatShapes(v.arg)]))
      return 'lnProduct';
  }
  if (w.arg.k === 'div') {
    const num = termShape(w.arg.a);
    const den = termShape(w.arg.b);
    for (const [p, q] of [
      [u, v],
      [v, u],
    ] as const)
      if (
        Q.eq(p.c, w.c) &&
        Q.eq(q.c, Q.neg(w.c)) &&
        termShape(p.arg) === num &&
        termShape(q.arg) === den
      )
        return 'lnQuotient';
  }
  return null;
}

/** la valeur rationnelle d'une écriture sans logarithme (« 3 », « (1 ÷ 2) × 1 », « 3 + 1 ÷ 2 ») */
function ratWrittenDeep(t: Term): Rat | null {
  if (t.k === 'add') {
    let s: Rat = Q.ZERO;
    for (const c of t.c) {
      const r = ratWrittenDeep(c);
      if (!r) return null;
      s = Q.add(s, r);
    }
    return s;
  }
  return ratFactor(t);
}

/**
 * Le geste de logarithme d'un maillon VRAI a → b (moteur v45), ou null : un terme de la
 * somme (ou deux qui se réunissent, ou un qui se sépare) transformé par UNE règle de ln,
 * les autres recopiés.
 */
export function claimsLnGesture(a: Term, b: Term): ClaimsLnGesture | null {
  const A = a.k === 'add' ? a.c : [a];
  const S = b.k === 'add' ? [...b.c] : [b];
  const R: Term[] = [];
  for (const t of A) {
    const i = S.findIndex((s) => termShape(s) === termShape(t));
    if (i >= 0) S.splice(i, 1);
    else R.push(t);
  }
  if (!R.length || !S.length) return null;
  const lr = R.map(lnTermOf);
  const ls = S.map(lnTermOf);
  if (R.length === 1 && S.length === 1) {
    const x = lr[0];
    const y = ls[0];
    if (x && y) return lnPair(x, y);
    // ln(1) = 0, ln(e) = 1, c ln(e³) = 3c : une valeur à connaître
    const lv = (l: LnTerm | null, other: Term): boolean => {
      if (!l) return false;
      const q = baseExponent(l.f, l.arg);
      const v = ratWrittenDeep(other);
      return !!q && !!v && Q.eq(Q.mul(l.c, q), v);
    };
    if (lv(x ?? null, S[0]!)) return 'lnValue';
    return null;
  }
  if (R.length === 1 && S.length === 2 && lr[0] && ls[0] && ls[1])
    return lnSplit(lr[0], ls[0], ls[1]);
  if (R.length === 2 && S.length === 1 && ls[0] && lr[0] && lr[1])
    return lnSplit(ls[0], lr[0], lr[1]);
  return null;
}

/** l'écriture contient un logarithme */
export function hasLn(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      return t.f === 'ln' || t.f === 'log' || hasLn(t.a);
    case 'add':
    case 'mul':
      return t.c.some(hasLn);
    case 'div':
    case 'pow':
    case 'binom':
      return hasLn(t.a) || hasLn(t.b);
    default:
      return false;
  }
}

/** n ≥ 2 est une puissance parfaite (mᵏ, k ≥ 2) */
export function isPerfectPower(n: bigint): boolean {
  if (n < 4n) return false;
  for (let k = 2n; 2n ** k <= n; k++) {
    // racine k-ième entière par dichotomie
    let lo = 1n;
    let hi = n;
    while (lo < hi) {
      const mid = (lo + hi + 1n) / 2n;
      if (mid ** k <= n) lo = mid;
      else hi = mid - 1n;
    }
    if (lo ** k === n) return true;
  }
  return false;
}

/**
 * Un logarithme pas encore simplifié (moteur v45) : ln(u) dont l'argument n'est pas un
 * entier ≥ 2 qui n'est pas une puissance parfaite — ln(8) = 3 ln(2), ln(1 ÷ 4), ln(√2),
 * ln(e), ln(1). Une telle écriture n'est pas une VALEUR de la feuille d'affirmations.
 */
export function reducibleLn(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      if (t.f === 'ln' || t.f === 'log') {
        const u = t.a;
        if (!isNum(u) || !Q.isInteger(u.v) || u.v.n < 2n) return true;
        // log(10) = 1
        if (t.f === 'log' && u.v.n === 10n) return true;
        return isPerfectPower(u.v.n);
      }
      return reducibleLn(t.a);
    case 'add':
    case 'mul':
      return t.c.some(reducibleLn);
    case 'div':
    case 'pow':
    case 'binom':
      return reducibleLn(t.a) || reducibleLn(t.b);
    default:
      return false;
  }
}

/** la constante e écrite seule (« e », « √e », « e³ », pas « exp », pas « e^… ») */
export const E_CONSTANT_RE = /(^|[^A-Za-zÀ-ÿ])e(?![A-Za-zÀ-ÿ^])/;

/**
 * Le corrigé d'une feuille d'affirmations applique-t-il une règle de ln (moteur v45) ?
 * Une telle feuille est en v45 : une app v44 garde ces lignes sans HORA.
 */
export function pathUsesClaimsLn(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map(exprOf);
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++) if (claimsLnGesture(ms[i]!, ms[i + 1]!)) return true;
  }
  return false;
}

/* ------------------------------ version ------------------------------ */

function exprOf(text: string): Term | null {
  const p = parseStatement(text.trim());
  return p.ok && p.s.k === 'expr' ? p.s.e : null;
}

/** le terme contient un cosinus, un sinus ou une tangente */
function hasTrig(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      return t.f === 'cos' || t.f === 'sin' || t.f === 'tan' || hasTrig(t.a);
    case 'add':
    case 'mul':
      return t.c.some(hasTrig);
    case 'div':
    case 'pow':
    case 'binom':
      return hasTrig(t.a) || hasTrig(t.b);
    default:
      return false;
  }
}

/**
 * Le corrigé d'une feuille d'affirmations montre-t-il un de ces gestes ? Une feuille dont
 * le corrigé s'en sert est en v37 : une app v36 ne sait pas les juger. (Ici, « b est une
 * valeur » se lit « b sans fonction trigonométrique » : au pire, une feuille passe en v37
 * sans en avoir besoin.)
 */
export function pathUsesClaimsGestures(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map(exprOf);
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++)
      if (claimsGesture(ms[i]!, ms[i + 1]!, !hasTrig(ms[i + 1]!))) return true;
  }
  return false;
}

/**
 * Le corrigé montre-t-il un geste du moteur v39 (décomposer, regrouper en puissance,
 * puissance de 10) ? Une telle feuille est en v39.
 */
export function pathUsesClaimsFormGestures(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map(exprOf);
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++) {
      const g = claimsFormGesture(ms[i]!, ms[i + 1]!);
      if (g && g !== 'euclid') return true;
    }
  }
  return false;
}

/** Le corrigé fait-il une division euclidienne (« 29 = 2 × 14 + 1 ») ? Une telle feuille est en v40. */
export function pathUsesClaimsEuclid(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map(exprOf);
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++)
      if (claimsFormGesture(ms[i]!, ms[i + 1]!) === 'euclid') return true;
  }
  return false;
}

/** une division par le nombre 0 écrite (« 3 ÷ 0 », jamais « 3 ÷ 0,5 ») */
export const ZERO_DIVISION_RE = /÷\s*0(?![,\d])/;

/** Les affirmations ou le corrigé écrivent-ils une division par 0 ? (valeur interdite, v37) */
export function textsUseZeroDivision(texts: readonly string[]): boolean {
  return texts.some((t) => ZERO_DIVISION_RE.test(t));
}
